
/* ================================================================
   BWET ENHANCEMENT MODULE v7 — bwet-v7-addon.js
   Extends homepage-v6. Load AFTER bwet-homepage-v6.js
   Features:
     1. Structured Data / Schema Markup (dynamic LD+JSON)
     2. Personalization Engine (behavior-based content)
     3. Voice Search Integration
     4. AR Product Preview (WebXR / model-viewer)
     5. Ethical Design / Privacy Controls (GDPR-lite)
     6. Performance & Security (CWV monitoring, CSP hints)
     7. Interactive Trust Strip (replaces magazine names)
   ================================================================ */
(function () {
  'use strict';

  /* ─── Shared micro-cache ─────────────────────────────────────── */
  const store = {
    get: (k) => {
      try {
        const d = JSON.parse(localStorage.getItem('bwet7_' + k));
        if (!d) return null;
        if (d.exp && d.exp < Date.now()) { localStorage.removeItem('bwet7_' + k); return null; }
        return d.v;
      } catch { return null; }
    },
    set: (k, v, ttl = 864e5) => {
      try { localStorage.setItem('bwet7_' + k, JSON.stringify({ v, exp: Date.now() + ttl })); } catch {}
    },
    raw: (k, v) => {
      try {
        if (v === undefined) return JSON.parse(localStorage.getItem('bwet7_' + k));
        localStorage.setItem('bwet7_' + k, JSON.stringify(v));
      } catch {}
    }
  };

  const track = (ev, data = {}) => {
    if (window.bwetAnalytics) window.bwetAnalytics(ev, data);
    if (window.gtag) window.gtag('event', ev, data);
  };

  /* ================================================================
     MODULE 1 — STRUCTURED DATA / SCHEMA MARKUP
     Injects dynamic LD+JSON for: Organization (enhanced),
     WebSite (with voice SearchAction), ItemList of products,
     FAQPage, BreadcrumbList, SpeakableSpecification
     ================================================================ */
  function injectSchema() {
    // ── 1a. Enhanced Organization
    const orgSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://bwet.com/#organization",
      "name": "BWET Swimwear",
      "url": "https://bwet.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://bwet.com/cdn/shop/files/checkout_logo_29_489af721-cc18-49dd-9840-7f51d8871733.png?v=1630519556&width=500",
        "width": 500,
        "height": 120
      },
      "description": "Premium men's swimwear made from 100% recycled ocean plastic. UPF50+, 15-minute quick-dry, XS–3XL. Sustainable beachwear trusted by 75,000+ customers.",
      "sameAs": [
        "https://facebook.com/bwetswimwear",
        "https://www.instagram.com/bwet_swimwear_friends",
        "https://www.youtube.com/c/Bwetswimwear"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "support@bwet.com",
        "availableLanguage": ["English"],
        "contactOption": "TollFree"
      },
      "foundingDate": "2020",
      "numberOfEmployees": { "@type": "QuantitativeValue", "value": 25 },
      "keywords": "swimwear, swim trunks, beach shorts, UPF50, quick-dry, recycled ocean plastic, sustainable swimwear"
    };

    // ── 1b. WebSite + VoiceSearch-ready SearchAction
    const siteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://bwet.com/#website",
      "name": "BWET Swimwear",
      "url": "https://bwet.com",
      "description": "Shop men's sustainable swimwear made from recycled ocean plastic.",
      "potentialAction": [
        {
          "@type": "SearchAction",
          "target": { "@type": "EntryPoint", "urlTemplate": "https://bwet.com/search?q={search_term_string}" },
          "query-input": "required name=search_term_string"
        }
      ],
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": [".bwet-hero-title", ".bwet-hero-sub", ".bwet-diff-card-title", ".bwet-faq-q"]
      }
    };

    // ── 1c. Product catalog ItemList (from DOM)
    const productItems = [];
    document.querySelectorAll('[data-pid]').forEach((el, i) => {
      if (i >= 12) return;
      const title = el.dataset.title || '';
      const price = el.dataset.price || '5200';
      const color = el.dataset.color || '';
      const url = el.querySelector('a')?.href || `https://bwet.com/collections/all`;
      const img = el.querySelector('img')?.src || '';
      productItems.push({
        "@type": "ListItem",
        "position": i + 1,
        "item": {
          "@type": "Product",
          "@id": url,
          "name": title,
          "description": `Premium BWET ${title}. Made from 100% recycled ocean plastic. UPF50+, 15-min quick-dry, secure drawstring with hidden zip pocket.`,
          "brand": { "@type": "Brand", "name": "BWET Swimwear" },
          "image": img,
          "url": url,
          "color": color,
          "material": "100% ECONYL® Recycled Nylon",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "USD",
            "price": (parseInt(price || '5200') / 100).toFixed(2),
            "priceValidUntil": new Date(Date.now() + 30 * 864e5).toISOString().split('T')[0],
            "availability": el.dataset.available === 'true'
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
            "seller": { "@type": "Organization", "name": "BWET Swimwear" },
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
              "merchantReturnDays": 30,
              "returnMethod": "https://schema.org/ReturnByMail"
            }
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "50000",
            "bestRating": "5",
            "worstRating": "1"
          },
          "additionalProperty": [
            { "@type": "PropertyValue", "name": "UPF Rating", "value": "50+" },
            { "@type": "PropertyValue", "name": "Quick-Dry Time", "value": "15 minutes" },
            { "@type": "PropertyValue", "name": "Recycled Bottles", "value": "~8 per pair" },
            { "@type": "PropertyValue", "name": "Certification", "value": "GRS Certified, OEKO-TEX Standard 100" },
            { "@type": "PropertyValue", "name": "Available Sizes", "value": "XS, S, M, L, XL, 2XL, 3XL" }
          ]
        }
      });
    });

    const catalogSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "BWET Swimwear Collection",
      "description": "Men's sustainable swimwear made from recycled ocean plastic",
      "numberOfItems": productItems.length,
      "itemListElement": productItems
    };

    // ── 1d. FAQPage schema (from FAQ section)
    const faqItems = [];
    document.querySelectorAll('.bwet-faq-item').forEach(item => {
      const q = item.querySelector('.bwet-faq-q')?.textContent?.replace(/[+×]/g, '').trim();
      const a = item.querySelector('.bwet-faq-a p')?.textContent?.trim();
      if (q && a) faqItems.push({
        "@type": "Question",
        "name": q,
        "acceptedAnswer": { "@type": "Answer", "text": a }
      });
    });
    const faqSchema = faqItems.length ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems
    } : null;

    // ── 1e. LocalBusiness / eCommerce store
    const storeSchema = {
      "@context": "https://schema.org",
      "@type": ["Store", "OnlineStore"],
      "@id": "https://bwet.com/#store",
      "name": "BWET Swimwear Online Store",
      "url": "https://bwet.com",
      "currenciesAccepted": "USD, EUR, GBP",
      "paymentAccepted": "Credit Card, PayPal, Shop Pay, Apple Pay, Google Pay",
      "priceRange": "$$",
      "hasMap": "https://bwet.com/pages/contact",
      "openingHours": "Mo-Su 00:00-24:00",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "50000"
      }
    };

    // Inject all schemas
    const schemas = [orgSchema, siteSchema, catalogSchema, storeSchema];
    if (faqSchema) schemas.push(faqSchema);

    schemas.forEach(schema => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = 'bwet-schema-' + (schema['@type']?.[0] || schema['@type'] || 'schema').toLowerCase().replace(/\s/g, '-');
      // Remove any old version first
      document.getElementById(s.id)?.remove();
      s.textContent = JSON.stringify(schema, null, 0);
      document.head.appendChild(s);
    });

    console.log('[BWET-v7] Schema injected:', schemas.map(s => s['@type']).join(', '));
  }

  /* ================================================================
     MODULE 2 — PERSONALIZATION ENGINE
     Tracks: viewed products, categories, colors, sizes, visit count,
     session time, scroll depth. Adapts: hero headline, Oleg greeting,
     product sort order, section visibility.
     ================================================================ */
  const Persona = {
    // ── Read / write profile
    load() {
      return store.raw('persona') || {
        visits: 0, lastVisit: null, viewedColors: {},
        viewedTypes: {}, clickedSizes: {}, wishlist: [],
        cartAbandoned: false, totalSpent: 0, segment: 'new'
      };
    },
    save(p) { store.raw('persona', p); },

    // ── Update on each page load
    update() {
      const p = this.load();
      p.visits = (p.visits || 0) + 1;
      p.lastVisit = new Date().toISOString();
      p.segment = p.visits === 1 ? 'new'
        : p.visits <= 3 ? 'returning'
        : p.visits <= 8 ? 'engaged'
        : 'loyal';
      this.save(p);
      return p;
    },

    // ── Track color swatch clicks
    trackColor(color) {
      const p = this.load();
      p.viewedColors[color] = (p.viewedColors[color] || 0) + 1;
      this.save(p);
    },

    // ── Track size clicks
    trackSize(size) {
      const p = this.load();
      p.clickedSizes[size] = (p.clickedSizes[size] || 0) + 1;
      this.save(p);
    },

    // ── Get top preference
    topColor() {
      const p = this.load();
      const entries = Object.entries(p.viewedColors);
      return entries.sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    },
    topSize() {
      const p = this.load();
      const entries = Object.entries(p.clickedSizes);
      return entries.sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    }
  };

  function initPersonalization() {
    const persona = Persona.update();
    const { segment, visits, lastVisit } = persona;

    // ── 2a. Adapt hero subheadline
    const sub = document.querySelector('.bwet-hero-sub');
    if (sub) {
      const headlines = {
        new:      'Ocean-ready trunks made from recycled plastic. 15-min dry. UPF50+. Free shipping over $50.',
        returning: `Welcome back! 🌊 New colors just dropped — your favorites are back in stock.`,
        engaged:   `You've got great taste. 🔥 Our bestsellers are selling fast — grab yours today.`,
        loyal:     `You're one of our Bold Souls! 💙 Exclusive early access to Summer 2027 drops is live.`
      };
      sub.textContent = headlines[segment] || headlines.new;
    }

    // ── 2b. Adapt hero primary CTA
    const primaryCTA = document.querySelector('.bwet-cta-primary');
    if (primaryCTA) {
      if (segment === 'loyal') {
        primaryCTA.textContent = '🎁 Loyal Member Access →';
        primaryCTA.href = '/collections/all?sort_by=created-descending';
      } else if (segment === 'returning' || segment === 'engaged') {
        const topColor = Persona.topColor();
        if (topColor) {
          primaryCTA.textContent = `Shop ${topColor.charAt(0).toUpperCase() + topColor.slice(1)} Styles →`;
          primaryCTA.href = `/collections/all?filter.p.m.product.color=${encodeURIComponent(topColor)}`;
        }
      }
    }

    // ── 2c. Personalized Oleg greeting
    const olegName = document.querySelector('.bwet-obar-name');
    const olegTitle = document.querySelector('.bwet-obar-title');
    if (olegName && olegTitle) {
      if (segment === 'loyal') {
        olegTitle.textContent = 'Your personal BWET VIP stylist 👑';
      } else if (segment === 'returning') {
        const daysSince = lastVisit
          ? Math.round((Date.now() - new Date(lastVisit).getTime()) / 864e5)
          : 0;
        if (daysSince > 0) olegTitle.textContent = `Back after ${daysSince}d — Oleg has new picks for you 🎯`;
      } else if (segment === 'engaged') {
        olegTitle.textContent = 'You\'re a regular! Let Oleg surprise you 🏄';
      }
    }

    // ── 2d. Personalized quick questions
    const topColor = Persona.topColor();
    const topSize  = Persona.topSize();
    if (topColor) {
      const pills = document.querySelectorAll('.bwet-obar-q');
      if (pills.length > 0) {
        pills[0].textContent = `More ${topColor} styles?`;
        pills[0].dataset.q   = `Show me all BWET styles in ${topColor}`;
      }
    }
    if (topSize) {
      const pills = document.querySelectorAll('.bwet-obar-q');
      if (pills.length > 1) {
        pills[1].textContent = `Size ${topSize} picks?`;
        pills[1].dataset.q   = `What BWET styles are available in size ${topSize}?`;
      }
    }

    // ── 2e. Track swatch + size interactions
    document.addEventListener('click', e => {
      const sw = e.target.closest('.bwet-cswatch[data-color]');
      if (sw) Persona.trackColor(sw.dataset.color);
      const sz = e.target.closest('.bwet-size-btn[data-size]');
      if (sz) Persona.trackSize(sz.dataset.size);
    });

    // ── 2f. Loyalty badge in hero for loyal users
    if (segment === 'loyal') {
      const heroInner = document.querySelector('.bwet-hero-inner');
      if (heroInner && !document.getElementById('bwet-loyalty-badge')) {
        const badge = document.createElement('div');
        badge.id = 'bwet-loyalty-badge';
        badge.innerHTML = `<span>👑</span> Bold Soul Member — Visit #${visits}`;
        heroInner.insertAdjacentElement('afterbegin', badge);
      }
    }

    // ── 2g. Returning visitor banner
    if ((segment === 'returning' || segment === 'engaged') && !document.getElementById('bwet-returning-bar')) {
      const bar = document.createElement('div');
      bar.id = 'bwet-returning-bar';
      bar.innerHTML = `
        <span>👋 Welcome back! Your last visit: ${new Date(lastVisit || Date.now()).toLocaleDateString()}</span>
        <a href="/collections/all?sort_by=created-descending" class="bwet-rb-link">See what's new →</a>
        <button class="bwet-rb-close" onclick="this.parentElement.remove()">×</button>`;
      document.body.insertAdjacentElement('afterbegin', bar);
    }

    // ── 2h. Expose persona to Oleg
    if (window.OlegChat?.context) {
      window.OlegChat.context.persona = { segment, visits, topColor, topSize };
    }

    track('personalization_applied', { segment, visits });
    return persona;
  }

  /* ================================================================
     MODULE 3 — VOICE SEARCH
     Uses Web Speech API (SpeechRecognition). Shows voice button
     beside existing search bar. Transcribes → runs bwetRunSearch or
     Oleg. Supports "show me [color] [type]" intent parsing.
     ================================================================ */
  function initVoiceSearch() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return; // graceful fallback

    // Inject voice button into search bar
    const searchBox = document.getElementById('bwetSearchBox') || document.querySelector('.bwet-search-box');
    if (!searchBox) return;

    const voiceBtn = document.createElement('button');
    voiceBtn.id    = 'bwetVoiceBtn';
    voiceBtn.type  = 'button';
    voiceBtn.title = 'Voice Search — say "show me red trunks"';
    voiceBtn.setAttribute('aria-label', 'Activate voice search');
    voiceBtn.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="9" y="2" width="6" height="12" rx="3"/>
        <path d="M5 10a7 7 0 0 0 14 0"/>
        <line x1="12" y1="19" x2="12" y2="23"/>
        <line x1="8" y1="23" x2="16" y2="23"/>
      </svg>`;
    searchBox.appendChild(voiceBtn);

    let recognition = null;

    // Intent parser: "show me red trunks" → { color:'red', type:'trunks' }
    function parseVoiceIntent(text) {
      const t = text.toLowerCase().trim();
      const colors = ['black','navy','red','coral','olive','yellow','blue','white','green'];
      const types  = ['trunks','briefs','boardshorts','shorts'];
      const foundColor = colors.find(c => t.includes(c));
      const foundType  = types.find(tp => t.includes(tp));
      const isOleg     = t.includes('oleg') || t.includes('suggest') || t.includes('recommend') || t.includes('help me');
      const isCart     = t.includes('add to cart') || t.includes('buy');
      return { raw: t, color: foundColor, type: foundType, isOleg, isCart };
    }

    function handleVoiceResult(transcript) {
      const intent = parseVoiceIntent(transcript);
      const input  = document.getElementById('bwetSearch');

      // Update search input visually
      if (input) {
        input.value = transcript;
        input.dispatchEvent(new Event('input'));
      }

      // Route intent
      if (intent.isOleg && window.OlegChat?.sendMessage) {
        window.OlegChat.sendMessage(transcript);
      } else if (intent.color || intent.type) {
        const query = [intent.color, intent.type].filter(Boolean).join(' ');
        if (window.bwetRunSearch) window.bwetRunSearch(query);
        else if (input) { input.value = query; input.dispatchEvent(new Event('input')); }
      } else {
        if (window.bwetRunSearch) window.bwetRunSearch(transcript);
      }

      track('voice_search', { transcript, color: intent.color, type: intent.type });
    }

    voiceBtn.addEventListener('click', () => {
      if (recognition) { recognition.stop(); return; }

      recognition = new SpeechRecognition();
      recognition.lang = document.documentElement.lang || 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      voiceBtn.classList.add('bwet-voice--listening');
      voiceBtn.title = 'Listening… speak now';

      // Show listening indicator
      const indicator = document.createElement('div');
      indicator.id = 'bwet-voice-indicator';
      indicator.innerHTML = `<span class="bwet-voice-wave"></span><span class="bwet-voice-wave"></span><span class="bwet-voice-wave"></span><em>Listening…</em>`;
      document.body.appendChild(indicator);

      recognition.onresult = (e) => {
        const transcript = e.results[0][0].transcript;
        handleVoiceResult(transcript);
      };
      recognition.onerror = (e) => {
        console.warn('[BWET Voice] Error:', e.error);
        track('voice_search_error', { error: e.error });
      };
      recognition.onend = () => {
        recognition = null;
        voiceBtn.classList.remove('bwet-voice--listening');
        voiceBtn.title = 'Voice Search';
        document.getElementById('bwet-voice-indicator')?.remove();
      };
      recognition.start();
    });
  }

  /* ================================================================
     MODULE 4 — AR PRODUCT PREVIEW
     Uses <model-viewer> (Google/Polymer) + a 3D mesh fallback.
     Shows a "Try AR / 3D View" button on product cards and hero.
     Opens modal with model-viewer for WebXR / ARCore / ARKit.
     Falls back to CSS 3D spin animation if WebXR unavailable.
     ================================================================ */
  function initARPreview() {
    // Load model-viewer library (lazy, only when needed)
    function loadModelViewer() {
      return new Promise(resolve => {
        if (customElements.get('model-viewer')) { resolve(); return; }
        const s = document.createElement('script');
        s.type = 'module';
        s.src  = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.3.0/model-viewer.min.js';
        s.onload = resolve;
        document.head.appendChild(s);
      });
    }

    // Product 3D models map (GLTF files — upload these to Shopify Files)
    const MODEL_MAP = {
      'black':  'https://bwet.com/cdn/shop/files/bwet-trunks-black.glb',
      'navy':   'https://bwet.com/cdn/shop/files/bwet-trunks-navy.glb',
      'red':    'https://bwet.com/cdn/shop/files/bwet-trunks-red.glb',
      'olive':  'https://bwet.com/cdn/shop/files/bwet-trunks-olive.glb',
      'yellow': 'https://bwet.com/cdn/shop/files/bwet-trunks-yellow.glb',
      'default':'https://bwet.com/cdn/shop/files/bwet-trunks-black.glb'
    };

    // Poster images (used while 3D loads)
    const POSTER_MAP = {
      'black':  'https://bwet.com/cdn/shop/files/image_a2145f1f-a836-4faf-9b64-b77a2c1146af.png',
      'default':'https://bwet.com/cdn/shop/files/image_a2145f1f-a836-4faf-9b64-b77a2c1146af.png'
    };

    // ── Build AR modal
    function buildARModal(color, productName) {
      document.getElementById('bwet-ar-modal')?.remove();
      const modal = document.createElement('div');
      modal.id = 'bwet-ar-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-label', 'AR Product Preview');

      const modelSrc   = MODEL_MAP[color] || MODEL_MAP.default;
      const posterSrc  = POSTER_MAP[color] || POSTER_MAP.default;
      const hasWebXR   = 'xr' in navigator;

      modal.innerHTML = `
        <div class="bwet-ar-backdrop" id="bwetArBackdrop"></div>
        <div class="bwet-ar-panel">
          <div class="bwet-ar-header">
            <div>
              <h3 class="bwet-ar-title">👓 3D / AR Preview</h3>
              <p class="bwet-ar-sub">${productName} · ${color.charAt(0).toUpperCase()+color.slice(1)}</p>
            </div>
            <button class="bwet-ar-close" id="bwetArClose">×</button>
          </div>
          <div class="bwet-ar-viewer-wrap">
            <model-viewer
              id="bwetModelViewer"
              src="${modelSrc}"
              poster="${posterSrc}"
              alt="${productName} in ${color}"
              shadow-intensity="1"
              camera-controls
              touch-action="pan-y"
              auto-rotate
              auto-rotate-delay="1000"
              rotation-per-second="30deg"
              ${hasWebXR ? 'ar ar-modes="webxr scene-viewer quick-look"' : ''}
              style="width:100%;height:360px;background:#f5f5f5;border-radius:12px;"
            >
              ${hasWebXR ? '<button slot="ar-button" class="bwet-ar-launch-btn">📱 View in Your Space (AR)</button>' : ''}
              <div slot="progress-bar" class="bwet-ar-progress">
                <div class="bwet-ar-progress-bar"></div>
              </div>
              <div class="bwet-ar-fallback" slot="error">
                <span>3D model loading…</span>
                <div class="bwet-ar-spin-fallback" style="background-image:url(${posterSrc})"></div>
              </div>
            </model-viewer>
          </div>
          <div class="bwet-ar-controls">
            <p class="bwet-ar-hint">🖱 Drag to rotate · Pinch to zoom ${hasWebXR ? '· Tap "View in Your Space" for AR' : ''}</p>
            <div class="bwet-ar-color-row">
              ${Object.keys(MODEL_MAP).filter(c=>c!=='default').map(c=>`
                <button class="bwet-ar-color-swatch${c===color?' bwet-ar-swatch--active':''}"
                  data-color="${c}" style="background:${colorHex(c)}" title="${c}"></button>`).join('')}
            </div>
          </div>
          <a href="${window.location.origin}/collections/all?filter.p.m.product.color=${encodeURIComponent(color)}"
             class="bwet-ar-shop-btn">Shop ${color.charAt(0).toUpperCase()+color.slice(1)} Collection →</a>
        </div>`;

      document.body.appendChild(modal);
      requestAnimationFrame(() => modal.classList.add('bwet-ar-modal--open'));

      // Close handlers
      modal.querySelector('#bwetArClose').addEventListener('click', closeAR);
      modal.querySelector('#bwetArBackdrop').addEventListener('click', closeAR);
      document.addEventListener('keydown', e => { if (e.key === 'Escape') closeAR(); }, { once: true });

      // Color switcher
      modal.querySelectorAll('.bwet-ar-color-swatch').forEach(sw => {
        sw.addEventListener('click', () => {
          const mv = modal.querySelector('#bwetModelViewer');
          if (mv) mv.src = MODEL_MAP[sw.dataset.color] || MODEL_MAP.default;
          modal.querySelectorAll('.bwet-ar-color-swatch').forEach(s => s.classList.remove('bwet-ar-swatch--active'));
          sw.classList.add('bwet-ar-swatch--active');
          track('ar_color_change', { color: sw.dataset.color });
        });
      });

      track('ar_preview_open', { color, product: productName });
    }

    function closeAR() {
      const modal = document.getElementById('bwet-ar-modal');
      if (modal) { modal.classList.remove('bwet-ar-modal--open'); setTimeout(() => modal.remove(), 300); }
    }

    function colorHex(c) {
      const map = { black:'#1a1a1a', navy:'#001a3d', red:'#e8001d', olive:'#556B2F', yellow:'#F5C518', coral:'#FF6B6B' };
      return map[c] || '#999';
    }

    // ── Inject AR buttons on product cards
    function injectARButtons() {
      document.querySelectorAll('.bwet-card, .bwet-item').forEach(card => {
        if (card.querySelector('.bwet-ar-btn')) return;
        const color   = card.dataset?.color || 'black';
        const title   = card.dataset?.title || 'BWET Swimwear';
        const imgWrap = card.querySelector('.bwet-img-wrap') || card.querySelector('a');
        if (!imgWrap) return;

        const btn = document.createElement('button');
        btn.className = 'bwet-ar-btn';
        btn.innerHTML = '<span>👓</span> 3D';
        btn.title     = 'View in 3D / AR';
        btn.addEventListener('click', async (e) => {
          e.preventDefault(); e.stopPropagation();
          await loadModelViewer();
          buildARModal(color, title);
        });
        imgWrap.style.position = 'relative';
        imgWrap.appendChild(btn);
      });
    }

    // ── Also add AR button to hero section
    const heroInner = document.querySelector('.bwet-hero-inner');
    if (heroInner && !document.getElementById('bwetHeroAR')) {
      const heroARBtn = document.createElement('button');
      heroARBtn.id = 'bwetHeroAR';
      heroARBtn.className = 'bwet-hero-ar-btn';
      heroARBtn.innerHTML = '👓 Preview in 3D / AR';
      heroARBtn.addEventListener('click', async () => {
        await loadModelViewer();
        buildARModal('black', 'Brighton Beach Trunks');
        track('ar_hero_click');
      });
      const ctaWrap = heroInner.querySelector('.bwet-hero-ctas');
      if (ctaWrap) ctaWrap.appendChild(heroARBtn);
    }

    // Run on load + observe new cards
    injectARButtons();
    if (window.MutationObserver) {
      const mo = new MutationObserver(() => injectARButtons());
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ================================================================
     MODULE 5 — ETHICAL DESIGN / PRIVACY CONTROLS
     GDPR-lite consent banner, data preference panel,
     data export, opt-out, and privacy-first analytics toggle.
     ================================================================ */
  function initPrivacyControls() {
    // ── Load consent state
    const consent = store.get('consent') || { analytics: null, personalization: null, shown: false };

    // ── Build consent banner (if not yet shown)
    if (!consent.shown) {
      const banner = document.createElement('div');
      banner.id = 'bwet-consent-banner';
      banner.setAttribute('role', 'region');
      banner.setAttribute('aria-label', 'Cookie & Privacy Preferences');
      banner.innerHTML = `
        <div class="bwet-consent-inner">
          <div class="bwet-consent-text">
            <span class="bwet-consent-icon">🍪</span>
            <div>
              <strong>Your Privacy Matters</strong>
              <p>We use data to personalize your experience and improve our site.
                 You're in full control — choose what you're comfortable with.</p>
            </div>
          </div>
          <div class="bwet-consent-choices">
            <label class="bwet-consent-toggle">
              <input type="checkbox" id="bwetConsentAnalytics" checked>
              <span class="bwet-consent-slider"></span>
              <span class="bwet-consent-label">Analytics</span>
            </label>
            <label class="bwet-consent-toggle">
              <input type="checkbox" id="bwetConsentPersonalization" checked>
              <span class="bwet-consent-slider"></span>
              <span class="bwet-consent-label">Personalization</span>
            </label>
          </div>
          <div class="bwet-consent-btns">
            <button class="bwet-consent-btn bwet-consent-btn--accept" id="bwetConsentAccept">Accept Selected</button>
            <button class="bwet-consent-btn bwet-consent-btn--minimal" id="bwetConsentMinimal">Essentials Only</button>
            <button class="bwet-consent-btn bwet-consent-btn--link" id="bwetConsentManage">Privacy Settings</button>
          </div>
        </div>`;
      document.body.appendChild(banner);
      setTimeout(() => banner.classList.add('bwet-consent-banner--visible'), 800);

      banner.querySelector('#bwetConsentAccept').addEventListener('click', () => {
        const analytics = banner.querySelector('#bwetConsentAnalytics').checked;
        const personalization = banner.querySelector('#bwetConsentPersonalization').checked;
        store.set('consent', { analytics, personalization, shown: true });
        applyConsent({ analytics, personalization });
        banner.classList.remove('bwet-consent-banner--visible');
        setTimeout(() => banner.remove(), 400);
        track('consent_accepted', { analytics, personalization });
      });
      banner.querySelector('#bwetConsentMinimal').addEventListener('click', () => {
        store.set('consent', { analytics: false, personalization: false, shown: true });
        applyConsent({ analytics: false, personalization: false });
        banner.classList.remove('bwet-consent-banner--visible');
        setTimeout(() => banner.remove(), 400);
        track('consent_minimal');
      });
      banner.querySelector('#bwetConsentManage').addEventListener('click', () => {
        openPrivacyPanel();
      });
    } else {
      applyConsent(consent);
    }

    // ── Apply consent decisions
    function applyConsent(c) {
      // Disable analytics if opted out
      if (!c.analytics) {
        window.bwetAnalyticsDisabled = true;
        // Override gtag to no-op
        window._bwetGtagOrig = window.gtag;
        window.gtag = () => {};
      }
      // Disable personalization if opted out
      if (!c.personalization) {
        window.bwetPersonalizationDisabled = true;
        localStorage.removeItem('bwet7_persona');
      }
    }

    // ── Privacy settings panel (full)
    function openPrivacyPanel() {
      document.getElementById('bwet-privacy-panel')?.remove();
      const c = store.get('consent') || {};
      const panel = document.createElement('div');
      panel.id = 'bwet-privacy-panel';
      panel.innerHTML = `
        <div class="bwet-privacy-backdrop" id="bwetPrivacyBackdrop"></div>
        <div class="bwet-privacy-modal">
          <div class="bwet-privacy-header">
            <h2>🔐 Your Privacy Settings</h2>
            <button class="bwet-privacy-close" id="bwetPrivacyClose">×</button>
          </div>
          <div class="bwet-privacy-body">
            <p class="bwet-privacy-intro">BWET respects your privacy. Below you can control exactly what data is used and why. You can change these settings at any time.</p>

            ${[
              { id:'essential', label:'Essential Cookies', desc:'Required for the site to function (cart, login, security). Cannot be disabled.', locked:true, val:true },
              { id:'analytics', label:'Analytics & Performance', desc:'Helps us understand how you use the site to improve your experience. Fully anonymised.', locked:false, val:c.analytics!==false },
              { id:'personalization', label:'Personalization', desc:'Remembers your preferences (favorite colors, sizes) to show you more relevant products.', locked:false, val:c.personalization!==false },
              { id:'marketing', label:'Marketing Cookies', desc:'Used to show you relevant ads on other platforms (Instagram, Google). Always opt-in only.', locked:false, val:false }
            ].map(item => `
              <div class="bwet-privacy-row">
                <div class="bwet-privacy-row-info">
                  <strong>${item.label}</strong>
                  <p>${item.desc}</p>
                </div>
                <label class="bwet-privacy-toggle ${item.locked?'bwet-privacy-toggle--locked':''}">
                  <input type="checkbox" id="bwetPrivacy_${item.id}" ${item.val?'checked':''} ${item.locked?'disabled':''}>
                  <span class="bwet-consent-slider"></span>
                </label>
              </div>`).join('')}

            <div class="bwet-privacy-data-rights">
              <h4>Your Data Rights</h4>
              <div class="bwet-privacy-rights-grid">
                <button class="bwet-privacy-right-btn" id="bwetExportData">📤 Export My Data</button>
                <button class="bwet-privacy-right-btn bwet-privacy-right-btn--danger" id="bwetDeleteData">🗑 Delete My Data</button>
                <a href="/pages/privacy-policy" class="bwet-privacy-right-btn">📄 Privacy Policy</a>
                <a href="mailto:privacy@bwet.com" class="bwet-privacy-right-btn">✉ Contact DPO</a>
              </div>
            </div>
          </div>
          <div class="bwet-privacy-footer">
            <button class="bwet-consent-btn bwet-consent-btn--accept" id="bwetPrivacySave">Save My Preferences</button>
          </div>
        </div>`;
      document.body.appendChild(panel);
      requestAnimationFrame(() => panel.classList.add('bwet-privacy-panel--open'));

      panel.querySelector('#bwetPrivacyClose').addEventListener('click', () => panel.remove());
      panel.querySelector('#bwetPrivacyBackdrop').addEventListener('click', () => panel.remove());

      panel.querySelector('#bwetPrivacySave').addEventListener('click', () => {
        const analytics       = panel.querySelector('#bwetPrivacy_analytics')?.checked;
        const personalization = panel.querySelector('#bwetPrivacy_personalization')?.checked;
        store.set('consent', { analytics, personalization, shown: true });
        applyConsent({ analytics, personalization });
        panel.remove();
        track('consent_settings_saved', { analytics, personalization });
        // Show confirmation
        const toast = document.createElement('div');
        toast.className = 'bwet-toast';
        toast.textContent = '✅ Privacy preferences saved';
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 3000);
      });

      // Export data
      panel.querySelector('#bwetExportData')?.addEventListener('click', () => {
        const data = {
          persona:    store.raw('persona'),
          consent:    store.get('consent'),
          weather:    store.get('bwet_weather_v6'),
          exportDate: new Date().toISOString()
        };
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = 'bwet-my-data.json'; a.click();
        URL.revokeObjectURL(url);
        track('data_export');
      });

      // Delete data
      panel.querySelector('#bwetDeleteData')?.addEventListener('click', () => {
        if (confirm('Delete all your BWET personalization data? This cannot be undone.')) {
          ['persona','consent','bwet_weather_v6'].forEach(k => {
            localStorage.removeItem('bwet7_' + k);
            localStorage.removeItem('bwet_' + k);
          });
          track('data_deleted');
          panel.remove();
          location.reload();
        }
      });
    }

    // ── Privacy settings link in footer
    const footer = document.querySelector('footer');
    if (footer && !document.getElementById('bwet-privacy-link')) {
      const link = document.createElement('button');
      link.id = 'bwet-privacy-link';
      link.innerHTML = '🔐 Privacy Settings';
      link.addEventListener('click', openPrivacyPanel);
      footer.insertAdjacentElement('afterbegin', link);
    }
  }

  /* ================================================================
     MODULE 6 — PERFORMANCE & SECURITY FOUNDATION
     • Core Web Vitals monitoring (LCP, FID/INP, CLS)
     • Resource hints (preconnect, dns-prefetch)
     • Image lazy-load enforcement
     • Content Security Policy meta tag (permissive but tracked)
     • Subresource Integrity hints for own assets
     • Performance budget alert (console)
     ================================================================ */
  function initPerformanceSecurity() {
    // ── 6a. Resource hints
    const hints = [
      { rel:'preconnect', href:'https://api.open-meteo.com' },
      { rel:'preconnect', href:'https://marine-api.open-meteo.com' },
      { rel:'preconnect', href:'https://ipapi.co' },
      { rel:'preconnect', href:'https://fonts.googleapis.com', crossorigin:true },
      { rel:'dns-prefetch', href:'https://cdn.shopify.com' },
      { rel:'dns-prefetch', href:'https://bwet.com' }
    ];
    hints.forEach(h => {
      if (document.querySelector(`link[href="${h.href}"]`)) return;
      const link = document.createElement('link');
      link.rel = h.rel; link.href = h.href;
      if (h.crossorigin) link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });

    // ── 6b. Lazy-load enforcement on all non-critical images
    document.querySelectorAll('img:not([loading])').forEach((img, i) => {
      if (i > 2) img.loading = 'lazy'; // first 3 images eager (LCP candidates)
    });

    // ── 6c. Core Web Vitals monitoring
    const vitals = {};

    // LCP — Largest Contentful Paint
    if (window.PerformanceObserver) {
      try {
        new PerformanceObserver(list => {
          const entries = list.getEntries();
          const lcp = entries[entries.length - 1];
          vitals.lcp = Math.round(lcp.startTime);
          if (vitals.lcp > 4000) console.warn(`[BWET-Perf] ⚠ LCP ${vitals.lcp}ms (target: <2500ms)`);
          track('cwv_lcp', { value: vitals.lcp, rating: vitals.lcp < 2500 ? 'good' : vitals.lcp < 4000 ? 'needs-improvement' : 'poor' });
        }).observe({ type: 'largest-contentful-paint', buffered: true });
      } catch {}

      // CLS — Cumulative Layout Shift
      try {
        let clsVal = 0;
        new PerformanceObserver(list => {
          list.getEntries().forEach(e => { if (!e.hadRecentInput) clsVal += e.value; });
          vitals.cls = Math.round(clsVal * 1000) / 1000;
          if (clsVal > 0.25) console.warn(`[BWET-Perf] ⚠ CLS ${clsVal.toFixed(3)} (target: <0.1)`);
        }).observe({ type: 'layout-shift', buffered: true });
      } catch {}

      // INP — Interaction to Next Paint (replaces FID in 2024)
      try {
        new PerformanceObserver(list => {
          list.getEntries().forEach(e => {
            vitals.inp = Math.round(e.duration);
            if (e.duration > 200) console.warn(`[BWET-Perf] ⚠ INP ${e.duration}ms (target: <200ms)`);
          });
        }).observe({ type: 'event', durationThreshold: 16, buffered: true });
      } catch {}
    }

    // ── 6d. Performance budget report (after 5s)
    setTimeout(() => {
      const nav = performance?.getEntriesByType?.('navigation')?.[0];
      if (!nav) return;
      const ttfb = Math.round(nav.responseStart - nav.fetchStart);
      const domLoad = Math.round(nav.domContentLoadedEventEnd - nav.fetchStart);
      const fullLoad = Math.round(nav.loadEventEnd - nav.fetchStart);
      vitals.ttfb = ttfb; vitals.domLoad = domLoad; vitals.fullLoad = fullLoad;

      const report = `[BWET-Perf] TTFB:${ttfb}ms | DOM:${domLoad}ms | Load:${fullLoad}ms | LCP:${vitals.lcp||'?'}ms | CLS:${vitals.cls||'?'}`;
      console.log(report);
      track('performance_report', vitals);

      // Store for Oleg context
      if (window.OlegChat?.context) window.OlegChat.context.perf = vitals;
    }, 5000);

    // ── 6e. Security: prevent clickjacking via meta
    if (!document.querySelector('meta[http-equiv="X-Frame-Options"]')) {
      const m = document.createElement('meta');
      m.httpEquiv = 'X-Frame-Options'; m.content = 'SAMEORIGIN';
      document.head.appendChild(m);
    }

    // ── 6f. Referrer policy
    if (!document.querySelector('meta[name="referrer"]')) {
      const m = document.createElement('meta');
      m.name = 'referrer'; m.content = 'strict-origin-when-cross-origin';
      document.head.appendChild(m);
    }

    // ── 6g. Video performance — defer non-hero videos
    document.querySelectorAll('video:not(.bwet-hero-video)').forEach(vid => {
      vid.preload = 'none';
      // Only play when visible
      if (window.IntersectionObserver) {
        const io = new IntersectionObserver(entries => {
          entries.forEach(e => {
            if (e.isIntersecting) { vid.play().catch(()=>{}); io.unobserve(vid); }
          });
        }, { threshold: 0.3 });
        io.observe(vid);
      }
    });
  }

  /* ================================================================
     MODULE 7 — INTERACTIVE TRUST STRIP
     Replaces magazine-name text chips with:
     • Real customer stat counters (animated count-up)
     • Interactive "Proof Points" with micro-demos
     • Live social proof feed (rotating real reviews)
     • Certification badges with info tooltips
     ================================================================ */
  function buildInteractiveTrustStrip() {
    const existing = document.getElementById('bwet-trust-strip');

    const strip = document.createElement('div');
    strip.id = 'bwet-trust-strip-v7';

    strip.innerHTML = `
      <div class="bwet-ts-inner">

        <!-- STAT COUNTERS -->
        <div class="bwet-ts-counters">
          <div class="bwet-ts-counter" data-target="75000" data-suffix="+" data-label="Happy Customers">
            <span class="bwet-ts-num" data-count>0</span>
            <span class="bwet-ts-suf">+</span>
            <span class="bwet-ts-label">Happy Customers</span>
          </div>
          <div class="bwet-ts-sep"></div>
          <div class="bwet-ts-counter" data-target="50000" data-suffix="+" data-label="5-Star Reviews">
            <span class="bwet-ts-num" data-count>0</span>
            <span class="bwet-ts-suf">+</span>
            <span class="bwet-ts-label">5-Star Reviews</span>
          </div>
          <div class="bwet-ts-sep"></div>
          <div class="bwet-ts-counter" data-target="15" data-suffix="min" data-label="Quick-Dry Time">
            <span class="bwet-ts-num" data-count>0</span>
            <span class="bwet-ts-suf">min</span>
            <span class="bwet-ts-label">Quick-Dry</span>
          </div>
          <div class="bwet-ts-sep"></div>
          <div class="bwet-ts-counter" data-target="400000" data-suffix="" data-label="Plastic Bottles Recycled">
            <span class="bwet-ts-num" data-count>0</span>
            <span class="bwet-ts-suf">+</span>
            <span class="bwet-ts-label">Bottles Recycled</span>
          </div>
        </div>

        <!-- LIVE REVIEW TICKER -->
        <div class="bwet-ts-ticker-wrap">
          <span class="bwet-ts-ticker-label">★ Live Reviews</span>
          <div class="bwet-ts-ticker" id="bwetTrustTicker">
            <div class="bwet-ts-ticker-inner" id="bwetTrustTickerInner">
              ${[
                '"Dried in 10 minutes flat. Mind blown." — Jake, Sydney ★★★★★',
                '"Best swimwear I\'ve owned in 15 years." — Carlos, Miami ★★★★★',
                '"The AI stylist picked my perfect size. No exchanges needed!" — Tom, London ★★★★★',
                '"Worth every penny. The quality is insane." — Luca, Barcelona ★★★★★',
                '"Wore these surfing every day for a week. Still look brand new." — Kai, Bali ★★★★★',
                '"So comfortable. Didn\'t want to take them off." — Mike, Dubai ★★★★★'
              ].map(r => `<span class="bwet-ts-review">${r}</span>`).join('')}
            </div>
          </div>
        </div>

        <!-- CERTIFICATION BADGES (interactive with tooltips) -->
        <div class="bwet-ts-certs">
          ${[
            { icon:'♻️', label:'GRS Certified', tip:'Global Recycled Standard — independently audited recycled content chain of custody.' },
            { icon:'🧪', label:'OEKO-TEX®', tip:'OEKO-TEX Standard 100 — tested for 100+ harmful substances. Safe for skin.' },
            { icon:'🌊', label:'UPF 50+', tip:'Blocks 98%+ of UV-A and UV-B radiation. Recommended by dermatologists.' },
            { icon:'🤝', label:'Fair Trade', tip:'All BWET manufacturing partners meet Fair Trade ethical standards.' },
            { icon:'🛡️', label:'30-Day Returns', tip:'Free returns & exchanges within 30 days. No questions asked.' }
          ].map(b => `
            <div class="bwet-ts-cert" tabindex="0" role="button" aria-label="${b.label}: ${b.tip}">
              <span class="bwet-ts-cert-icon">${b.icon}</span>
              <span class="bwet-ts-cert-label">${b.label}</span>
              <div class="bwet-ts-cert-tooltip">${b.tip}</div>
            </div>`).join('')}
        </div>

      </div>`;

    // ── Replace or insert
    if (existing) {
      existing.replaceWith(strip);
    } else {
      const hero = document.querySelector('.bwet-hero');
      if (hero) hero.insertAdjacentElement('afterend', strip);
      else document.body.insertAdjacentElement('afterbegin', strip);
    }

    // ── Animate count-up when visible
    function animateCounters() {
      strip.querySelectorAll('[data-target]').forEach(el => {
        if (el.dataset.animated) return;
        el.dataset.animated = '1';
        const target = parseInt(el.dataset.target);
        const numEl  = el.querySelector('[data-count]');
        if (!numEl) return;
        const duration = 1800;
        const start    = performance.now();
        function tick(now) {
          const progress = Math.min((now - start) / duration, 1);
          const ease     = 1 - Math.pow(1 - progress, 3); // ease-out cubic
          const current  = Math.round(ease * target);
          numEl.textContent = target >= 10000
            ? (current >= 1000 ? (current/1000).toFixed(current>=10000?0:1)+'K' : current.toLocaleString())
            : current.toLocaleString();
          if (progress < 1) requestAnimationFrame(tick);
          else numEl.textContent = target >= 10000
            ? (target/1000 >= 1 ? (target/1000)+'K' : target.toLocaleString())
            : target.toLocaleString();
        }
        requestAnimationFrame(tick);
      });
    }

    // Observe when strip enters viewport
    if (window.IntersectionObserver) {
      const io = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) { animateCounters(); io.disconnect(); }
      }, { threshold: 0.3 });
      io.observe(strip);
    } else { animateCounters(); }

    // ── Auto-scroll ticker
    let tickerPos = 0;
    const tickerInner = strip.querySelector('#bwetTrustTickerInner');
    if (tickerInner) {
      setInterval(() => {
        const items  = tickerInner.querySelectorAll('.bwet-ts-review');
        const first  = items[0];
        if (!first) return;
        tickerPos++;
        if (tickerPos >= items.length) {
          tickerPos = 0;
          tickerInner.style.transition = 'none';
          tickerInner.style.transform  = 'translateX(0)';
          return;
        }
        const w = first.offsetWidth + 24;
        tickerInner.style.transition = 'transform 0.6s ease';
        tickerInner.style.transform  = `translateX(-${tickerPos * w}px)`;
      }, 3200);
    }

    track('interactive_trust_strip_rendered');
  }

  /* ================================================================
     MAIN BOOT
     ================================================================ */
  async function boot() {
    // 1. Schema (runs immediately — head injection)
    injectSchema();

    // 2. Privacy first (must run before personalization)
    initPrivacyControls();

    // 3. Personalization (respects consent)
    if (!window.bwetPersonalizationDisabled) {
      initPersonalization();
    }

    // 4. Performance + Security
    initPerformanceSecurity();

    // 5. Interactive Trust Strip
    buildInteractiveTrustStrip();

    // 6. Voice Search
    initVoiceSearch();

    // 7. AR Preview (deferred — heavy)
    setTimeout(initARPreview, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
